import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import { Linking, Pressable, StyleSheet, Text, View } from 'react-native';
import { api } from '~/api';
import { errorMessage } from '~/api/client';
import { BookingDetailsCard } from '~/components/BookingDetailsCard';
import { Screen } from '~/components/Screen';
import { TopNav } from '~/components/TopNav';
import { colors, fontFamily, maxContentWidth, radii, space, type } from '~/ui/tokens';

type StatusData = NonNullable<Api.Payment.Status.Response['data']>;

const SUPPORT_EMAIL = 'customercare@suitescanner.com';

const PAYMENT_SETTLED: StatusData['paymentStatus'][] = ['successful', 'failed', 'refunded', 'refund_pending'];
const BOOKING_SETTLED: StatusData['bookingStatus'][] = ['confirmed', 'failed', 'rate_expired'];
const SAFE_EXIT_DELAY_MS = 15_000;

export default function PaymentConfirmedScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ orderId?: string; partnerOrderId?: string; transactionId?: string }>();
  const [status, setStatus] = useState<StatusData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSafeExit, setShowSafeExit] = useState(false);
  const transactionId = params.transactionId || '';
  const partnerOrderId = params.partnerOrderId || '';

  const refresh = useCallback(async (): Promise<StatusData | null> => {
    if (!transactionId && !partnerOrderId) return null;
    try {
      const response = await api.payment.status({ transactionId, partnerOrderId });
      if (response.data) {
        setStatus(response.data);
        setError(null);
        return response.data;
      }
      setError(errorMessage(response, 'We could not refresh this status.'));
      return null;
    } catch {
      setError('We could not refresh this status. We’ll keep trying.');
      return null;
    }
  }, [transactionId, partnerOrderId]);

  // Poll while this screen is mounted. A browser return can leave navigation
  // focus unchanged, so a focus-only effect can miss the final booking update.
  useEffect(() => {
      if (!transactionId && !partnerOrderId) return;

      let cancelled = false;
      let timer: ReturnType<typeof setTimeout> | null = null;
      const startedAt = Date.now();
      // Supplier confirmation usually takes one to two minutes: poll quickly
      // at first, then back off, and slowly while the tab is in the background
      // (coming back to it refreshes at once, below).
      const nextDelay = (delayed: boolean) => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return 10_000;
        if (delayed) return 6000;
        const elapsed = Date.now() - startedAt;
        return elapsed < 20_000 ? 1500 : elapsed < 60_000 ? 3000 : 5000;
      };

      const run = async () => {
        if (cancelled) return;
        const data = await refresh();
        if (cancelled) return;
        if (!data) {
          timer = setTimeout(run, 2500);
          return;
        }
        // A failed payment means a booking will never be attempted — booking
        // stays "not_initiated" forever, so it must not gate stopping here too.
        const bookingDone = BOOKING_SETTLED.includes(data.bookingStatus) || data.paymentStatus === 'failed';
        const settled = PAYMENT_SETTLED.includes(data.paymentStatus) && bookingDone
          && (Boolean(data.booking) || data.paymentStatus === 'failed' || data.bookingStatus === 'rate_expired');
        if (settled) return;
        timer = setTimeout(run, nextDelay(data.bookingStatus === 'delayed'));
      };

      run();
      const onVisible = () => {
        if (document.visibilityState === 'visible' && !cancelled) void refresh();
      };
      if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
      return () => {
        cancelled = true;
        if (timer) clearTimeout(timer);
        if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
      };
  }, [refresh, transactionId, partnerOrderId]);

  const paymentSettled = Boolean(status) && PAYMENT_SETTLED.includes(status!.paymentStatus);
  // A failed payment means a booking will never be attempted — booking stays
  // "not_initiated" forever, so it must not read as "still working" either.
  const bookingSettled = Boolean(status) && (BOOKING_SETTLED.includes(status!.bookingStatus) || status!.paymentStatus === 'failed');
  const stillWorking = !paymentSettled || !bookingSettled;

  // A safety valve: if nothing has resolved after a while, offer an exit
  // instead of trapping the user here indefinitely.
  useEffect(() => {
    if (!stillWorking) { setShowSafeExit(false); return; }
    const timer = setTimeout(() => setShowSafeExit(true), SAFE_EXIT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [stillWorking, transactionId, partnerOrderId]);

  // The page heading reflects the actual booking state, not the phone-keyed
  // dev scenario — it just happens that only some scenarios ever reach
  // "confirmed" during a test run.
  const pageHeading = status?.paymentStatus === 'failed' ? 'Your payment didn’t go through'
    : status?.bookingStatus === 'confirmed' && status?.paymentStatus === 'successful' ? 'Your booking is confirmed'
    : status?.bookingStatus === 'failed' || status?.bookingStatus === 'rate_expired' ? 'Your booking couldn’t be completed'
    : 'We’re finalising your stay';
  const heroStatus = overallStatus(status);

  const embeddedOrder: Wire.OrderSummary | null = status?.booking ? {
    orderId: status.orderId ?? 0,
    partnerOrderId: status.booking.partnerOrderId,
    status: status.paymentStatus === 'failed' ? 'payment_failed'
      : status.bookingStatus === 'failed' || status.bookingStatus === 'rate_expired' ? 'failed'
      : status.bookingStatus === 'confirmed' && status.paymentStatus === 'successful' ? 'confirmed'
      : 'processing',
    hotelAddress: status.booking.hotelAddress,
    hotelStarRating: status.booking.hotelStarRating,
    hotelReviewScore: status.booking.hotelReviewScore,
    hotelReviewCount: status.booking.hotelReviewCount,
    cancelledAt: status.booking.cancelledAt,
    paymentAmount: status.booking.paymentAmount,
    refundStatus: status.booking.refundStatus,
    refundAmount: status.booking.refundAmount,
    paymentStatus: status.paymentStatus === 'successful' ? 'paid' : status.paymentStatus === 'failed' ? 'failed' : status.paymentStatus === 'refunded' ? 'refunded' : status.paymentStatus === 'refund_pending' ? 'refund_pending' : 'pending',
    isCancellable: status.booking.isCancellable,
    freeCancellationBefore: status.booking.freeCancellationBefore,
    cancellationPenalties: status.booking.cancellationPenalties ?? null,
    hotelId: '',
    hotelName: status.booking.hotelName,
    thumbnailUrl: status.booking.thumbnailUrl,
    checkIn: status.booking.checkIn,
    checkOut: status.booking.checkOut,
    roomName: status.booking.roomName,
    roomCount: status.booking.roomCount,
    guestCount: status.booking.guestCount,
    total: status.booking.total,
    inrTotal: (status.booking as any).inrTotal,
    usdTotal: (status.booking as any).usdTotal,
  } : null;

  return <Screen><TopNav /><View style={styles.page}>
    <View style={styles.stack}>
      {!embeddedOrder ? <><HeroStatusIcon status={heroStatus} /><Text style={styles.heading}>{pageHeading}</Text></> : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}

      {embeddedOrder ? (
        <BookingDetailsCard order={embeddedOrder} bookingRef={status?.orderId ? `SS-${status.orderId}` : null} />
      ) : (
        <View style={styles.safeExit}>
          <Text style={styles.safeExitText}>
            {status?.paymentStatus === 'failed' ? 'Your payment was not completed. No hotel booking was made.'
              : status?.bookingStatus === 'rate_expired' ? 'This room is no longer available.'
                : 'Checking your payment and preparing your booking details…'}
          </Text>
          {status?.paymentStatus === 'failed' || status?.bookingStatus === 'rate_expired' ? <SupportLine /> : null}
        </View>
      )}
      {showSafeExit && stillWorking && !embeddedOrder ? (
        <View style={styles.safeExit}>
          <Text style={styles.safeExitText}>This is taking longer than expected. You can leave this page and check your booking status under My Bookings.</Text>
          <Pressable accessibilityRole="button" onPress={() => router.push('/trips')} style={styles.safeExitLink}>
            <Text style={styles.safeExitLinkText}>Go to My Bookings</Text>
          </Pressable>
        </View>
      ) : null}
    </View>
  </View></Screen>;
}

type HeroStatus = 'pending' | 'success' | 'error';

// One combined status for the page-level hero icon — a hard failure on
// either side wins (nothing to wait for any more), full confirmation only
// once the booking itself is actually confirmed, everything else reads as
// still in progress.
function overallStatus(status: StatusData | null): HeroStatus {
  if (!status) return 'pending';
  if (status.paymentStatus === 'failed' || status.bookingStatus === 'failed' || status.bookingStatus === 'rate_expired') return 'error';
  if (status.bookingStatus === 'confirmed' && status.paymentStatus === 'successful') return 'success';
  return 'pending';
}

function HeroStatusIcon({ status }: { status: HeroStatus }) {
  const backgroundColor = status === 'success' ? colors.green : status === 'error' ? colors.pink : colors.brand;
  const icon = status === 'success' ? 'checkmark' : status === 'error' ? 'close' : 'time';
  return (
    <View style={[styles.heroIcon, { backgroundColor }]}>
      <Ionicons name={icon} size={28} color={colors.white} />
    </View>
  );
}

function SupportLine() {
  return (
    <Text style={styles.copy}>
      For more information, contact us at{' '}
      <Text
        style={styles.link}
        accessibilityRole="link"
        onPress={() => Linking.openURL(`mailto:${SUPPORT_EMAIL}`)}
      >
        {SUPPORT_EMAIL}
      </Text>.
    </Text>
  );
}

const styles = StyleSheet.create({
  page: { width: '100%', maxWidth: maxContentWidth, alignSelf: 'center', paddingHorizontal: space.card, paddingTop: space.cardLarge, paddingBottom: space.sectionMajor },
  stack: { width: '100%', maxWidth: 680, alignSelf: 'center', gap: space.internal },
  heroIcon: { width: 60, height: 60, borderRadius: radii.full, alignItems: 'center', justifyContent: 'center', alignSelf: 'center' },
  heading: { fontFamily: fontFamily.bold, ...type.pageTitle, color: colors.ink, textAlign: 'center' },
  error: { fontFamily: fontFamily.medium, ...type.bodyRegular, color: '#B42318', backgroundColor: '#FFF1F0', borderRadius: radii.md, padding: space.compact },
  copy: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
  link: { fontFamily: fontFamily.semibold, color: colors.brand },
  safeExit: { backgroundColor: colors.skyTint, borderRadius: radii.md, borderWidth: 1, borderColor: colors.line, padding: space.internal, gap: space.related, alignItems: 'flex-start' },
  safeExitText: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
  safeExitLink: { paddingVertical: space.micro },
  safeExitLinkText: { fontFamily: fontFamily.semibold, ...type.bodyRegular, color: colors.brand },
});
